// Shared UI primitives for the integrated Clockora module.
// Ported from clockora/src/components/{ui,rolling-number,tilt,pagination,live-clock,time-sync,theme}.tsx
// as plain JSX, styled by the scoped clockora.css.

import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { clockoraGet } from "./api";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Loader2,
  Moon,
  Sun,
  Wifi,
  X,
} from "lucide-react";
import clsx from "clsx";
import { hueOf, initials, PAGE_SIZE_OPTIONS } from "./lib";

// ---------------- Modal ----------------

export function Modal({ open, onClose, title, subtitle, width = 540, children }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="modal-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal-panel w-full" style={{ maxWidth: width }}>
        <div className="flex items-start justify-between gap-4 border-b px-6 pt-5 pb-4" style={{ borderColor: "var(--line)" }}>
          <div>
            <h2 className="font-display text-[19px] font-bold tracking-tight">{title}</h2>
            {subtitle && <p className="mt-0.5 text-[13px] text-[var(--muted)]">{subtitle}</p>}
          </div>
          <button onClick={onClose} className="btn btn-ghost btn-sm !rounded-xl !p-2" aria-label="Close dialog">
            <X size={16} />
          </button>
        </div>
        <div className="px-6 py-5">{children}</div>
      </div>
    </div>
  );
}

// ---------------- Field ----------------

export function Field({ label, required, hint, children, icon: Icon }) {
  return (
    <div>
      <label className="label flex items-center gap-1.5">
        {Icon && <Icon size={12} className="text-[rgb(var(--c-yellow))]" />}
        {label}
        {required && <span className="text-[rgb(var(--c-rose))]">*</span>}
      </label>
      {children}
      {hint && <p className="mt-1.5 text-[11.5px] text-[var(--faint)]">{hint}</p>}
    </div>
  );
}

// ---------------- Select ----------------

export function SelectInput({ value, onChange, children, className }) {
  return (
    <div className={clsx("relative", className)}>
      <select className="input cursor-pointer" value={value} onChange={(e) => onChange(e.target.value)}>
        {children}
      </select>
      <ChevronDown size={15} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[var(--faint)]" />
    </div>
  );
}

// ---------------- Avatar ----------------

export function Avatar({ name, size = 38, seed }) {
  return (
    <span
      className="avatar"
      style={{
        "--h": hueOf(seed ?? name),
        width: size,
        height: size,
        fontSize: Math.max(10, size * 0.36),
        borderRadius: Math.max(8, size * 0.3),
      }}
    >
      {initials(name)}
    </span>
  );
}

// ---------------- Switch ----------------

export function Switch({ checked, onChange, disabled, label, size = "md" }) {
  const w = size === "sm" ? 38 : 46;
  const h = size === "sm" ? 21 : 26;
  const knob = h - 6;
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label ?? (checked ? "Deactivate account" : "Activate account")}
      title={checked ? "Active — click to deactivate" : "Inactive — click to activate"}
      disabled={disabled}
      onClick={(e) => {
        e.stopPropagation();
        onChange(!checked);
      }}
      className={clsx("switch", checked && "on", disabled && "is-disabled")}
      style={{ width: w, height: h }}
    >
      <span className="switch-knob" style={{ width: knob, height: knob, left: checked ? w - knob - 3 : 3 }} />
    </button>
  );
}

// ---------------- Empty state ----------------

export function EmptyState({ icon: Icon, title, body, action }) {
  return (
    <div className="flex flex-col items-center gap-3 px-6 py-14 text-center">
      <span className="icon-tile" style={{ width: 52, height: 52, borderRadius: 16 }}>
        <Icon size={24} />
      </span>
      <p className="font-display text-[16.5px] font-bold">{title}</p>
      <p className="max-w-sm text-[13.5px] leading-relaxed text-[var(--muted)]">{body}</p>
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}

// ---------------- Chip ----------------

export function Chip({ tone, children, className }) {
  return <span className={clsx("chip", `chip-${tone}`, className)}>{children}</span>;
}

// ---------------- Section header ----------------

export function SectionHeader({ eyebrow, title, right, eyebrowTone = "yellow" }) {
  return (
    <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
      <div>
        <p className={clsx("eyebrow", eyebrowTone === "blue" && "eyebrow-blue")}>{eyebrow}</p>
        <h2 className="font-display mt-1 text-[21px] font-bold tracking-tight">{title}</h2>
      </div>
      {right}
    </div>
  );
}

// ---------------- Rolling number (odometer) ----------------

const REPS = 6;
const CELLS = Array.from({ length: REPS * 10 }, (_, k) => String(k % 10));

export function RollingNumber({ text, className }) {
  const chars = String(text).split("");
  const mounted = useRef(false);
  const [pos, setPos] = useState(() => chars.map(() => 0));
  const [dur, setDur] = useState(() => chars.map(() => 0));

  useEffect(() => {
    const digitsTotal = chars.join("").replace(/\D/g, "").length;
    let digitIdx = -1;

    setPos((prev) => {
      let di = -1;
      return chars.map((c, i) => {
        if (!/\d/.test(c)) return 0;
        di++;
        const significance = digitsTotal - 1 - di;
        const digit = Number(c);
        if (!mounted.current) return digit + 10 * (3 + significance);
        const cur = prev[i] ?? 0;
        let next = Math.floor(cur / 10) * 10 + digit;
        while (next <= cur) next += 10;
        return next;
      });
    });

    setDur(
      chars.map((c) => {
        if (!/\d/.test(c)) return 0;
        digitIdx++;
        const significance = digitsTotal - 1 - digitIdx;
        return mounted.current ? 500 : 1150 + significance * 240;
      }),
    );

    mounted.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);

  return (
    <span className={clsx("rolling-num", className)} aria-label={text} role="text">
      {chars.map((c, i) =>
        /\d/.test(c) ? (
          <span key={i} className="roller-col" aria-hidden>
            <span
              className="roller-strip"
              style={{
                transform: `translateY(-${pos[i] ?? 0}em)`,
                transitionDuration: `${dur[i] ?? 0}ms`,
              }}
            >
              {CELLS.map((d, k) => (
                <span key={k} className="roller-cell">
                  {d}
                </span>
              ))}
            </span>
          </span>
        ) : (
          <span key={i} aria-hidden>
            {c}
          </span>
        ),
      )}
    </span>
  );
}

// ---------------- Tilt card ----------------

export function TiltCard({
  children,
  className,
  innerClassName,
  intensity = 9,
  onClick,
  style,
  tilt = true,
}) {
  const inner = useRef(null);

  const handleMove = (e) => {
    if (!tilt) return;
    const el = inner.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width;
    const py = (e.clientY - rect.top) / rect.height;
    const ry = (px - 0.5) * intensity;
    const rx = -(py - 0.5) * intensity;
    el.classList.add("tilting");
    el.style.transform = `rotateX(${rx.toFixed(2)}deg) rotateY(${ry.toFixed(2)}deg) scale3d(1.015,1.015,1)`;
    el.style.setProperty("--gx", `${(px * 100).toFixed(1)}%`);
    el.style.setProperty("--gy", `${(py * 100).toFixed(1)}%`);
  };

  const handleLeave = () => {
    if (!tilt) return;
    const el = inner.current;
    if (!el) return;
    el.classList.remove("tilting");
    el.style.transform = "";
  };

  return (
    <div className={clsx("tilt-outer", className)}>
      <div
        ref={inner}
        className={clsx("tilt-inner card", innerClassName)}
        style={style}
        onMouseMove={handleMove}
        onMouseLeave={handleLeave}
        onClick={onClick}
      >
        <span className="tilt-glare" aria-hidden />
        {children}
      </div>
    </div>
  );
}

// ---------------- Time sync (backend-synced clock) ----------------

const TimeSyncCtx = createContext({ offsetMs: 0, source: "device" });

/**
 * Syncs the UI clock to the backend's authoritative time (UTC+8).
 * Falls back silently to the device clock if the request fails.
 */
export function TimeSyncProvider({ axiosInstance, children }) {
  const [state, setState] = useState({ offsetMs: 0, source: "device" });

  useEffect(() => {
    let alive = true;
    const sync = async () => {
      try {
        const t0 = Date.now();
        const j = await clockoraGet(axiosInstance, "time");
        const t1 = Date.now();
        const remoteMs = j?.now ? new Date(j.now).getTime() : j?.epochMs;
        if (!alive || !Number.isFinite(remoteMs)) return;
        const rtt = (t1 - t0) / 2;
        setState({
          offsetMs: remoteMs + rtt - t1,
          source: j.source === "internet" ? "internet" : "server",
        });
      } catch {
        /* keep the previous offset */
      }
    };
    void sync();
    const t = window.setInterval(() => void sync(), 60_000);
    return () => {
      alive = false;
      window.clearInterval(t);
    };
  }, [axiosInstance]);

  return <TimeSyncCtx.Provider value={state}>{children}</TimeSyncCtx.Provider>;
}

export function useClockoraNow(intervalMs = 1000) {
  const { offsetMs } = useContext(TimeSyncCtx);
  const [now, setNow] = useState(() => new Date(Date.now() + offsetMs));

  useEffect(() => {
    const tick = () => setNow(new Date(Date.now() + offsetMs));
    tick();
    const t = window.setInterval(tick, intervalMs);
    return () => window.clearInterval(t);
  }, [intervalMs, offsetMs]);

  return now;
}

export function TimeBadge({ className }) {
  const { source } = useContext(TimeSyncCtx);
  return (
    <span
      className={clsx("chip chip-muted", className)}
      title="Clock is synced to server time — Asia/Manila (UTC+8)"
    >
      <Wifi size={11} />
      UTC+8 · {source === "internet" ? "internet time" : source === "server" ? "server time" : "syncing…"}
    </span>
  );
}

// ---------------- Live clock ----------------

export function LiveClock({ className, size = 30, showSeconds = true }) {
  const now = useClockoraNow(1000);
  // shift the synced instant into UTC+8 wall time
  const z = new Date(now.getTime() + 8 * 3_600_000);
  const h24 = z.getUTCHours();
  const hour = h24 % 12 === 0 ? 12 : h24 % 12;
  const hh = String(hour).padStart(2, "0");
  const mm = String(z.getUTCMinutes()).padStart(2, "0");
  const ss = String(z.getUTCSeconds()).padStart(2, "0");
  const ampm = h24 >= 12 ? "PM" : "AM";

  return (
    <span className={`lcd ${className ?? ""}`} style={{ fontSize: size, lineHeight: 1 }}>
      {hh}
      <span className="lcd-colon">:</span>
      {mm}
      {showSeconds && (
        <>
          <span className="lcd-colon">:</span>
          <span style={{ opacity: 0.85 }}>{ss}</span>
        </>
      )}
      <span style={{ fontSize: size * 0.42, marginLeft: size * 0.18, opacity: 0.75, fontWeight: 600 }}>{ampm}</span>
    </span>
  );
}

// ---------------- Pagination ----------------

export function pageWindow(current, totalPages, span = 1) {
  if (totalPages <= 1) return [1];
  const pages = new Set([1, totalPages]);
  for (let i = current - span; i <= current + span; i++) {
    if (i >= 1 && i <= totalPages) pages.add(i);
  }
  if (current <= 3) [2, 3, 4].forEach((n) => n <= totalPages && pages.add(n));
  if (current >= totalPages - 2)
    [totalPages - 1, totalPages - 2, totalPages - 3].forEach((n) => n >= 1 && pages.add(n));

  const sorted = [...pages].sort((a, b) => a - b);
  const out = [];
  let prev = 0;
  for (const p of sorted) {
    if (prev && p - prev > 1) out.push("gap");
    out.push(p);
    prev = p;
  }
  return out;
}

export function Pagination({
  page,
  pageSize,
  total,
  totalPages,
  from,
  to,
  busy = false,
  itemLabel = "events",
  onPageChange,
  onPageSizeChange,
}) {
  // Defensive fallbacks so a caller who forgets a prop never throws
  // "Cannot read properties of undefined (reading 'toLocaleString')".
  const safePage = Math.max(1, Number(page) || 1);
  const safePageSize = Math.max(1, Number(pageSize) || 25);
  const safeTotal = Math.max(0, Number(total) || 0);
  const safeTotalPages = Math.max(1, Number(totalPages) || 1);
  const safeFrom = safeTotal === 0 ? 0 : Math.max(1, Number(from) || (safePage - 1) * safePageSize + 1);
  const safeTo = safeTotal === 0 ? 0 : Math.min(safeTotal, Number(to) || safePage * safePageSize);
  const canPrev = safePage > 1;
  const canNext = safePage < safeTotalPages;

  return (
    <nav
      className="mt-4 flex flex-col gap-3 border-t pt-4 lg:flex-row lg:items-center lg:justify-between"
      style={{ borderColor: "var(--line)" }}
      aria-label="Pagination"
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[12.5px] text-[var(--muted)]">
        <span>
          Showing <span className="tnum font-bold text-[var(--ink)]">{safeTotal === 0 ? 0 : safeFrom}</span>–
          <span className="tnum font-bold text-[var(--ink)]">{safeTo}</span> of{" "}
          <span className="tnum font-bold text-[var(--ink)]">{safeTotal.toLocaleString()}</span> {itemLabel}
        </span>
        {busy && (
          <span className="flex items-center gap-1.5 text-[11.5px] text-[var(--faint)]">
            <Loader2 size={12} className="animate-spin" />
            updating
          </span>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 lg:justify-end">
        <label className="flex items-center gap-2 text-[12.5px] text-[var(--muted)]">
          <span className="hidden sm:inline">Rows per page</span>
          <span className="sm:hidden">Rows</span>
          <select
            className="input tnum !w-auto !py-1.5 !pr-7 !text-[12.5px]"
            value={safePageSize}
            onChange={(e) => onPageSizeChange && onPageSizeChange(Number(e.target.value))}
            aria-label="Rows per page"
          >
            {PAGE_SIZE_OPTIONS.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>

        <div className="flex items-center gap-1">
          <PagerBtn label="First page" onClick={() => onPageChange && onPageChange(1)} disabled={!canPrev}>
            <ChevronsLeft size={15} />
          </PagerBtn>
          <PagerBtn label="Previous page" onClick={() => onPageChange && onPageChange(safePage - 1)} disabled={!canPrev}>
            <ChevronLeft size={15} />
          </PagerBtn>

          <div className="hidden items-center gap-1 sm:flex">
            {pageWindow(safePage, safeTotalPages).map((p, i) =>
              p === "gap" ? (
                <span key={`gap-${i}`} className="px-1 text-[12.5px] text-[var(--faint)]">
                  …
                </span>
              ) : (
                <button
                  key={p}
                  onClick={() => onPageChange && onPageChange(p)}
                  aria-label={`Page ${p}`}
                  aria-current={p === safePage ? "page" : undefined}
                  className={clsx("page-btn tnum", p === safePage && "on")}
                >
                  {p}
                </button>
              ),
            )}
          </div>
          <span className="tnum px-2 text-[12.5px] font-semibold text-[var(--muted)] sm:hidden">
            {safePage} / {safeTotalPages}
          </span>

          <PagerBtn label="Next page" onClick={() => onPageChange && onPageChange(safePage + 1)} disabled={!canNext}>
            <ChevronRight size={15} />
          </PagerBtn>
          <PagerBtn label="Last page" onClick={() => onPageChange && onPageChange(safeTotalPages)} disabled={!canNext}>
            <ChevronsRight size={15} />
          </PagerBtn>
        </div>
      </div>
    </nav>
  );
}

function PagerBtn({ children, label, onClick, disabled }) {
  return (
    <button className="page-btn" onClick={onClick} disabled={disabled} aria-label={label} title={label}>
      {children}
    </button>
  );
}

// ---------------- Theme toggle ----------------

export function ThemeToggle({ compact = false, className, light = false, onToggle }) {
  const toggle = () => {
    if (typeof onToggle === "function") onToggle();
  };

  // Compact icon button — used in the Clockora header band.
  return (
    <button
      onClick={toggle}
      className={clsx("btn btn-ghost btn-sm !rounded-xl !p-2.5", className)}
      title={light ? "Switch to dark mode" : "Switch to light mode"}
      aria-label="Toggle theme"
    >
      {light ? <Moon size={16} /> : <Sun size={16} />}
    </button>
  );
}
